/**
 * add-knowledge-dialog.tsx — Hộp "Thêm kiến thức" cho AI (Tài liệu cho AI).
 *
 * Các thẻ, chữ dễ hiểu cho người không rành công nghệ:
 *   • Tải file        — kéo thả nhiều file PDF / Word / Excel / CSV / TXT, hiện tiến độ từng file;
 *   • Link web / Google Docs — dán link trang web hoặc Google Docs đã chia sẻ công khai;
 *   • Hỏi đáp        — câu hỏi chuẩn + nhiều câu hỏi tương tự cùng một câu trả lời; nhập / xuất CSV;
 *   • Gõ tay          — tự nhập nội dung;
 *   • Google Sheets   — nhập bảng (link Google Sheets hoặc dán CSV) vào kho kiến thức / sản phẩm;
 *   • Sản phẩm        — bật / tắt sản phẩm cho AI tra cứu.
 * Máy chủ đọc tài liệu + chia đoạn + nhúng NỀN; hộp này theo dõi từng mục tới khi "Sẵn sàng" rồi báo
 * "Đã thêm N đoạn kiến thức từ … — hỏi thử AI ngay". Đóng hộp giữa chừng không sao: việc vẫn chạy.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  CheckCircle2, FileUp, Link2, Loader2, MessageCircleQuestion, MessageSquareText, PencilLine, RefreshCw, Search, Sheet, ShoppingBag,
  Trash2, XCircle, Database, ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { openInNewTab } from '@/components/ai/open-in-new-tab'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch, Separator } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api, apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import {
  uploadKnowledgeFile, ingestKnowledgeUrl, fetchKnowledgeEntry, type KnowledgeEntry,
} from '@/hooks/use-knowledge'
import { useKnowledgeBases } from '@/hooks/use-knowledge-bases'
import { KnowledgeBaseSelect } from './knowledge/kb-base-select'
import { KbCategorySelect } from './knowledge/kb-category-select'
import {
  useProducts, useUpdateProduct, useProductCategories, formatProductPrice,
  isSyncedProduct, PRODUCT_SOURCE_LABELS,
} from '@/hooks/use-products'
import { useAiBots } from '@/hooks/use-ai'
import {
  KB_ACCEPT, checkKbFile, embedStatusInfo, formatBytes, ingestSuccessMessage, isKbBusy,
} from '@/lib/kb-status'
import { QaCreatePanel } from './train/qa-form'

// ── Theo dõi từng mục nạp ─────────────────────────────────────────────────────

type ItemState = 'invalid' | 'waiting' | 'uploading' | 'processing' | 'done' | 'error'

interface IngestItem {
  key: string
  origin: 'file' | 'link' | 'text'
  name: string
  size?: number
  file?: File
  state: ItemState
  progress: number
  entryId?: string
  chunkCount?: number
  message?: string
}

let seq = 0
const nextKey = () => `k${++seq}`

/** Theo dõi các mục đang xử lý nền (đọc tài liệu + nhúng) — hỏi máy chủ mỗi 2 giây. */
function useTrackItems(items: IngestItem[], setItems: React.Dispatch<React.SetStateAction<IngestItem[]>>) {
  const qc = useQueryClient()
  const tracking = items.filter((i) => i.state === 'processing' && i.entryId).map((i) => i.entryId!).join(',')
  useEffect(() => {
    if (!tracking) return
    const ids = tracking.split(',')
    const t = setInterval(async () => {
      const rows = await Promise.all(ids.map((id) => fetchKnowledgeEntry(id).catch(() => null)))
      const byId = new Map(rows.filter(Boolean).map((r) => [r!.id, r!]))
      let changed = false
      setItems((prev) => prev.map((it) => {
        const r = it.entryId ? byId.get(it.entryId) : undefined
        if (!r || it.state !== 'processing' || isKbBusy(r.embedStatus)) return it
        changed = true
        const info = embedStatusInfo(r)
        if (r.embedStatus === 'ready') return { ...it, state: 'done', chunkCount: r.chunkCount ?? 0, name: it.origin === 'link' ? (r.title || it.name) : it.name, message: undefined }
        // Chưa có khoá nhúng nền tảng: mục đã lưu, AI vẫn tra theo từ khoá → coi là xong (kèm ghi chú), không phải lỗi.
        if (r.embedStatus === 'no_key') return { ...it, state: 'done', chunkCount: 0, name: it.origin === 'link' ? (r.title || it.name) : it.name, message: info.label }
        return { ...it, state: 'error', message: info.label }
      }))
      if (changed) qc.invalidateQueries({ queryKey: ['knowledge'] })
    }, 2_000)
    return () => clearInterval(t)
  }, [tracking, setItems, qc])
}

function ItemRow({ it, onRemove }: { it: IngestItem; onRemove?: () => void }) {
  const { t } = useTranslation('aiKnow')
  const icon = it.state === 'done' ? <CheckCircle2 className="h-4 w-4 text-success" />
    : it.state === 'error' || it.state === 'invalid' ? <XCircle className="h-4 w-4 text-destructive" />
      : it.state === 'waiting' ? <FileUp className="h-4 w-4 text-muted-foreground" />
        : <Loader2 className="h-4 w-4 animate-spin text-primary" />
  const status = it.state === 'uploading' ? t('addKnowledge.item.uploading', { n: it.progress })
    : it.state === 'processing' ? t('addKnowledge.item.processing')
      : it.state === 'waiting' ? t('addKnowledge.item.waiting')
        : it.state === 'done' ? ingestSuccessMessage(it.name, it.chunkCount)
          : it.message ?? t('addKnowledge.item.error')
  return (
    <li className="rounded-md border px-2.5 py-2">
      <div className="flex items-center gap-2">
        {icon}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{it.name}</span>
        {it.size != null && <span className="shrink-0 text-[11px] text-muted-foreground">{formatBytes(it.size)}</span>}
        {onRemove && (it.state === 'waiting' || it.state === 'invalid') && (
          <button type="button" title={t('addKnowledge.item.remove')} className="rounded p-0.5 text-muted-foreground hover:bg-muted" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <p className={cn('mt-0.5 pl-6 text-xs', it.state === 'done' ? 'text-success' : it.state === 'error' || it.state === 'invalid' ? 'text-destructive' : 'text-muted-foreground')}>
        {status}
      </p>
      {(it.state === 'uploading' || it.state === 'processing') && (
        <div className="ml-6 mt-1 h-1 overflow-hidden rounded bg-muted">
          <div
            className={cn('h-full bg-primary transition-all', it.state === 'processing' && 'animate-pulse')}
            style={{ width: `${it.state === 'processing' ? 100 : it.progress}%` }}
          />
        </div>
      )}
    </li>
  )
}

// ── Hộp chính ──────────────────────────────────────────────────────────────────

export type AddKnowledgeTab = 'qa' | 'file' | 'link' | 'text' | 'sheet' | 'product'
type TabKey = AddKnowledgeTab

const TAB_TITLES: Partial<Record<TabKey, string>> = {
  get file() { return i18n.t('aiKnow:addKnowledge.tabTitle.file') },
  get text() { return i18n.t('aiKnow:addKnowledge.tabTitle.text') },
  get link() { return i18n.t('aiKnow:addKnowledge.tabTitle.link') },
  get sheet() { return i18n.t('aiKnow:addKnowledge.tabTitle.sheet') },
  get qa() { return i18n.t('aiKnow:addKnowledge.tabTitle.qa') },
}

export function CreateKnowledgeDialog({
  defaultCategoryId = '', knowledgeBaseId, defaultKnowledgeBaseId = '', tabs, initialTab, onClose, onTryChat,
}: {
  defaultCategoryId?: string
  /** Kho cố định (mở từ trang một kho) — không cho đổi kho. */
  knowledgeBaseId?: string
  /** Kho chọn sẵn (vẫn đổi được) — vd kho đầu tiên nhân viên AI đang dùng. */
  defaultKnowledgeBaseId?: string
  /** Chỉ hiện các thẻ này (mặc định: tất cả). */
  tabs?: TabKey[]
  initialTab?: TabKey
  onClose: () => void
  /** Bấm "Hỏi thử AI" — mặc định mở màn huấn luyện của nhân viên AI đầu tiên (có khung Chat thử). */
  onTryChat?: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const { data: botsData } = useAiBots()
  const { data: bases } = useKnowledgeBases()
  const shownTabs: TabKey[] = tabs?.length ? tabs : ['file', 'link', 'qa', 'text', 'sheet', 'product']
  const [tab, setTab] = useState<TabKey>(initialTab ?? shownTabs[0])
  const [pickedBase, setPickedBase] = useState(defaultKnowledgeBaseId)
  // Kho nhận: kho cố định → kho đã chọn → kho đầu tiên của tổ chức ('' = máy chủ dùng "Kho mặc định").
  const baseId = knowledgeBaseId || pickedBase || bases?.[0]?.id || ''
  const [categoryId, setCategoryId] = useState(defaultCategoryId)
  const one = shownTabs.length === 1
  const [items, setItems] = useState<IngestItem[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [running, setRunning] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  // Link
  const [url, setUrl] = useState('')
  // Gõ tay
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)

  useTrackItems(items, setItems)

  const tryChat = useCallback(() => {
    onClose()
    if (onTryChat) { onTryChat(); return }
    const bot = botsData?.bots?.[0]
    // Màn nhân viên AI là màn khác (đang ở Kho tri thức) → mở tab mới, trang này giữ nguyên.
    openInNewTab(bot ? `/ai/nhan-vien/${bot.id}#kien-thuc` : '/ai/nhan-vien')
  }, [onClose, onTryChat, botsData])

  function addFiles(list: FileList | File[]) {
    const add: IngestItem[] = Array.from(list).map((f) => {
      const err = checkKbFile(f)
      return { key: nextKey(), origin: 'file' as const, name: f.name, size: f.size, file: f, state: err ? 'invalid' : 'waiting', progress: 0, message: err ?? undefined }
    })
    setItems((prev) => [...prev, ...add])
  }

  const patch = (key: string, p: Partial<IngestItem>) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...p } : i)))

  async function uploadAll() {
    const queue = items.filter((i) => i.state === 'waiting' && i.file)
    if (!queue.length) { toast.error(t('addKnowledge.noFiles')); return }
    setRunning(true)
    // Tải lần lượt 2 file một lúc — nhanh mà không làm nghẽn mạng yếu.
    const worker = async () => {
      for (let it = queue.shift(); it; it = queue.shift()) {
        const cur = it
        patch(cur.key, { state: 'uploading', progress: 0 })
        try {
          const entry = await uploadKnowledgeFile(cur.file!, {
            categoryId, knowledgeBaseId: baseId || null, onProgress: (pct) => patch(cur.key, { progress: pct }),
          })
          patch(cur.key, { state: 'processing', entryId: entry.id })
        } catch (err) {
          patch(cur.key, { state: 'error', message: apiError(err) })
        }
      }
    }
    await Promise.all([worker(), worker()])
    setRunning(false)
    qc.invalidateQueries({ queryKey: ['knowledge'] })
  }

  async function submitLink() {
    const u = url.trim()
    if (!u) { toast.error(t('addKnowledge.pasteUrl')); return }
    if (/docs\.google\.com\/spreadsheets\//i.test(u)) { toast.info(t('addKnowledge.isSheet')); setTab('sheet'); return }
    const key = nextKey()
    setItems((prev) => [...prev, { key, origin: 'link', name: u, state: 'uploading', progress: 100 }])
    try {
      const entry = await ingestKnowledgeUrl(u, categoryId, baseId || null)
      patch(key, { state: 'processing', entryId: entry.id, name: entry.title || u })
      setUrl('')
    } catch (err) {
      patch(key, { state: 'error', message: apiError(err) })
    }
  }

  async function submitText() {
    if (!content.trim()) { toast.error(t('addKnowledge.noContent')); return }
    setBusy(true)
    try {
      const { data } = await api.post<{ data: KnowledgeEntry }>('/knowledge', {
        type: 'description', format: 'article', risk: 'low', source: 'staff_manual',
        title: title.trim() || null,
        content: title.trim() ? `# ${title.trim()}\n\n${content.trim()}` : content.trim(),
        categoryId: categoryId || null,
        knowledgeBaseId: baseId || null,
      })
      const name = title.trim() || t('addKnowledge.typedContent')
      setItems((prev) => [...prev, { key: nextKey(), origin: 'text', name, state: 'processing', progress: 100, entryId: data.data.id }])
      setTitle(''); setContent('')
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  const doneCount = items.filter((i) => i.state === 'done').length
  const activeCount = items.filter((i) => i.state === 'uploading' || i.state === 'processing').length
  const waitingCount = items.filter((i) => i.state === 'waiting').length
  const shownItems = items.filter((i) => i.origin === tab)

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{one ? TAB_TITLES[shownTabs[0]] ?? t('addKnowledge.title') : t('addKnowledge.title')}</DialogTitle>
          <DialogDescription>
            {t('addKnowledge.description')}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          {!one && (
            <TabsList className="h-auto w-full flex-wrap justify-start gap-0.5">
              {shownTabs.includes('file') && <TabsTrigger value="file" className="gap-1.5"><FileUp className="h-3.5 w-3.5" /> {t('addKnowledge.tabs.file')}</TabsTrigger>}
              {shownTabs.includes('link') && <TabsTrigger value="link" className="gap-1.5"><Link2 className="h-3.5 w-3.5" /> {t('addKnowledge.tabs.link')}</TabsTrigger>}
              {shownTabs.includes('qa') && <TabsTrigger value="qa" className="gap-1.5"><MessageCircleQuestion className="h-3.5 w-3.5" /> {t('addKnowledge.tabs.qa')}</TabsTrigger>}
              {shownTabs.includes('text') && <TabsTrigger value="text" className="gap-1.5"><PencilLine className="h-3.5 w-3.5" /> {t('addKnowledge.tabs.text')}</TabsTrigger>}
              {shownTabs.includes('sheet') && <TabsTrigger value="sheet" className="gap-1.5"><Sheet className="h-3.5 w-3.5" /> {t('addKnowledge.tabs.sheet')}</TabsTrigger>}
              {shownTabs.includes('product') && <TabsTrigger value="product" className="gap-1.5"><ShoppingBag className="h-3.5 w-3.5" /> {t('addKnowledge.tabs.product')}</TabsTrigger>}
            </TabsList>
          )}

          {tab !== 'product' && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <div>
                <Label>{t('addKnowledge.baseLabel')}</Label>
                {knowledgeBaseId ? (
                  <p className="flex h-9 items-center truncate rounded-md border bg-muted/40 px-3 text-sm">📚 {bases?.find((b) => b.id === knowledgeBaseId)?.name ?? t('addKnowledge.currentBase')}</p>
                ) : (
                  <KnowledgeBaseSelect value={baseId} onChange={(id) => { setPickedBase(id); setCategoryId('') }} />
                )}
              </div>
              {tab !== 'sheet' && tab !== 'qa' && (
                <div>
                  <Label>{t('addKnowledge.folderLabel')}</Label>
                  <KbCategorySelect knowledgeBaseId={baseId} kind="article" value={categoryId} onChange={setCategoryId} noneLabel={t('addKnowledge.noFolder')} />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground sm:col-span-2">
                {t('addKnowledge.baseHint')}
              </p>
            </div>
          )}

          {/* ── Tải file ── */}
          <TabsContent value="file" className="space-y-3">
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click() }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files) }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
                dragOver ? 'border-primary bg-primary/5' : 'hover:border-primary/60 hover:bg-muted/40',
              )}
            >
              <FileUp className="h-8 w-8 text-primary" />
              <p className="text-sm font-medium">{t('addKnowledge.dropHere')}</p>
              <p className="text-xs text-muted-foreground">{t('addKnowledge.dropFormats')}</p>
            </div>
            <input
              ref={fileRef} type="file" multiple accept={KB_ACCEPT} hidden
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = '' }}
            />
            <p className="text-[11px] text-muted-foreground">
              {t('addKnowledge.fileTip')}
            </p>
            {shownItems.length > 0 && (
              <ul className="space-y-1.5">
                {shownItems.map((it) => (
                  <ItemRow key={it.key} it={it} onRemove={() => setItems((prev) => prev.filter((x) => x.key !== it.key))} />
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <Button onClick={() => void uploadAll()} disabled={running || waitingCount === 0}>
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                {running ? t('addKnowledge.uploading') : waitingCount > 0 ? t('addKnowledge.uploadN', { n: waitingCount }) : t('common:actions.upload')}
              </Button>
            </div>
          </TabsContent>

          {/* ── Link web / Google Docs ── */}
          <TabsContent value="link" className="space-y-3">
            <div>
              <Label>{t('addKnowledge.urlLabel')}</Label>
              <div className="flex gap-2">
                <Input
                  value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t('addKnowledge.urlPlaceholder')}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitLink() } }}
                />
                <Button onClick={() => void submitLink()}><Link2 className="h-4 w-4" /> {t('common:actions.add')}</Button>
              </div>
            </div>
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
              <li><Trans t={t} i18nKey="addKnowledge.linkNote.web" components={{ b: <b /> }} /></li>
              <li><Trans t={t} i18nKey="addKnowledge.linkNote.gdoc" components={{ b: <b /> }} /></li>
              <li>{t('addKnowledge.linkNote.file')}</li>
            </ul>
            {shownItems.length > 0 && <ul className="space-y-1.5">{shownItems.map((it) => <ItemRow key={it.key} it={it} />)}</ul>}
          </TabsContent>

          {/* ── Hỏi đáp: câu hỏi chuẩn + câu hỏi tương tự + câu trả lời; nhập / xuất CSV ── */}
          <TabsContent value="qa">
            {/* Danh mục Hỏi đáp là cây riêng — không mang thư mục Tài liệu sang. */}
            <QaCreatePanel key={baseId} knowledgeBaseId={baseId} defaultCategoryId="" />
          </TabsContent>

          {/* ── Gõ tay ── */}
          <TabsContent value="text" className="space-y-3">
            <div>
              <Label>{t('addKnowledge.textTitle')}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('addKnowledge.textTitlePlaceholder')} />
            </div>
            <div>
              <Label>{t('addKnowledge.textContent')}</Label>
              <textarea
                className="min-h-40 w-full rounded-md border bg-transparent p-2 text-sm outline-none"
                value={content} onChange={(e) => setContent(e.target.value)}
                placeholder={t('addKnowledge.textContentPlaceholder')}
              />
              <p className="text-right text-[11px] text-muted-foreground">{t('addKnowledge.charCount', { n: content.length.toLocaleString(currentLocale()) })}</p>
            </div>
            {shownItems.length > 0 && <ul className="space-y-1.5">{shownItems.map((it) => <ItemRow key={it.key} it={it} />)}</ul>}
            <div className="flex justify-end">
              <Button onClick={() => void submitText()} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PencilLine className="h-4 w-4" />} {t('addKnowledge.addKnowledge')}
              </Button>
            </div>
          </TabsContent>

          {/* ── Google Sheets / bảng ── */}
          <TabsContent value="sheet">
            <SheetImportPanel key={baseId} knowledgeBaseId={baseId} defaultCategoryId={categoryId} onDone={onClose} />
          </TabsContent>

          {/* ── Sản phẩm ── */}
          <TabsContent value="product">
            <ProductPickerInline />
          </TabsContent>
        </Tabs>

        {(doneCount > 0 || activeCount > 0) && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-success/30 bg-success/5 px-3 py-2">
            <p className="min-w-0 flex-1 text-sm">
              {activeCount > 0 ? t('addKnowledge.activeCount', { n: activeCount }) : ''}
              {activeCount > 0 && doneCount > 0 ? ' · ' : ''}
              {doneCount > 0 ? t('addKnowledge.doneCount', { n: doneCount }) : ''}
            </p>
            {doneCount > 0 && (
              <Button size="sm" onClick={tryChat} title={onTryChat ? undefined : t('addKnowledge.tryChatNewTab')}><MessageSquareText className="h-4 w-4" /> {t('addKnowledge.tryChat')}{!onTryChat && <ExternalLink className="h-3 w-3 opacity-70" aria-hidden="true" />}</Button>
            )}
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Nhập bảng: Google Sheets / dán CSV ──────────────────────────────────────────

const MAPPING_PRESETS: Record<'product' | 'knowledge', string> = {
  product: '{ "name": "Tên", "code": "Mã", "price": "Giá", "description": "Mô tả" }', // i18n-ignore: tên cột mẫu trong bảng tính (dữ liệu)
  knowledge: '{ "title": "Tiêu đề", "content": "Nội dung" }', // i18n-ignore: tên cột mẫu trong bảng tính (dữ liệu)
}

/** Bảng mẫu khớp với tên cột mẫu ở MAPPING_PRESETS (dữ liệu, không dịch). */
const CSV_PLACEHOLDER = 'Tiêu đề,Nội dung\nChính sách đổi trả,Đổi trong 7 ngày...' // i18n-ignore

function SheetImportPanel({ knowledgeBaseId, defaultCategoryId, onDone }: { knowledgeBaseId: string; defaultCategoryId: string; onDone: () => void }) {
  const { t } = useTranslation('aiKnow')
  const { data: prodCats } = useProductCategories()
  const qc = useQueryClient()
  const [importKind, setImportKind] = useState<'product' | 'knowledge'>('knowledge')
  const [source, setSource] = useState<'sheet' | 'csv'>('sheet')
  const [sheetUrl, setSheetUrl] = useState('')
  const [csv, setCsv] = useState('')
  const [mapping, setMapping] = useState(MAPPING_PRESETS.knowledge)
  const [categoryId, setCategoryId] = useState(defaultCategoryId)
  const [busy, setBusy] = useState(false)

  async function submit() {
    if (source === 'sheet' && !sheetUrl.trim()) { toast.error(t('addKnowledge.sheet.pasteLink')); return }
    if (source === 'csv' && !csv.trim()) { toast.error(t('addKnowledge.sheet.pasteData')); return }
    let mapObj: Record<string, string>
    try { mapObj = JSON.parse(mapping) } catch { toast.error(t('addKnowledge.sheet.badMapping')); return }
    setBusy(true)
    try {
      const { data } = await api.post('/product-knowledge/import', {
        kind: importKind,
        ...(source === 'sheet' ? { sheetUrl: sheetUrl.trim() } : { csv }),
        mapping: mapObj,
        categoryId: categoryId || undefined,
        ...(importKind === 'knowledge' ? { knowledgeBaseId: knowledgeBaseId || undefined } : {}),
      })
      const r = data?.data ?? {}
      toast.success(t('addKnowledge.sheet.imported', { created: r.created ?? 0, updated: r.updated ?? 0 }) + (r.skipped ? t('addKnowledge.sheet.importedSkipped', { n: r.skipped }) : ''))
      qc.invalidateQueries({ queryKey: ['knowledge'] })
      onDone()
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3 pt-1">
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label>{t('addKnowledge.sheet.importInto')}</Label>
          <Select value={importKind} onValueChange={(v) => { const k = v as 'product' | 'knowledge'; setImportKind(k); setMapping(MAPPING_PRESETS[k]); setCategoryId('') }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="knowledge">{t('addKnowledge.sheet.toKnowledge')}</SelectItem>
              <SelectItem value="product">{t('addKnowledge.sheet.toProducts')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t('addKnowledge.sheet.categoryLabel')}</Label>
          {importKind === 'knowledge' ? (
            <KbCategorySelect knowledgeBaseId={knowledgeBaseId} kind="article" value={categoryId} onChange={setCategoryId} noneLabel={t('addKnowledge.noFolder')} />
          ) : (
            <Select value={categoryId || '__none__'} onValueChange={(v) => setCategoryId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('addKnowledge.sheet.noCategory')}</SelectItem>
                {(prodCats ?? []).map((c: { id: string; name: string }) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <div className="flex gap-1 text-xs">
        <Button size="sm" variant={source === 'sheet' ? 'default' : 'outline'} onClick={() => setSource('sheet')}>{t('addKnowledge.sheet.sheetLink')}</Button>
        <Button size="sm" variant={source === 'csv' ? 'default' : 'outline'} onClick={() => setSource('csv')}>{t('addKnowledge.sheet.pasteCsv')}</Button>
      </div>
      {source === 'sheet' ? (
        <div>
          <Label>{t('addKnowledge.sheet.sheetLink')}</Label>
          <Input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." />
          <p className="mt-1 text-[11px] text-muted-foreground">{t('addKnowledge.sheet.sheetHint')}</p>
        </div>
      ) : (
        <div>
          <Label>{t('addKnowledge.sheet.csvLabel')}</Label>
          <textarea className="min-h-24 w-full rounded-md border bg-transparent p-2 font-mono text-xs outline-none" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={CSV_PLACEHOLDER} />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between">
          <Label>{t('addKnowledge.sheet.mappingLabel')}</Label>
          <button type="button" className="text-xs text-primary hover:underline" onClick={() => setMapping(MAPPING_PRESETS[importKind])}>{t('addKnowledge.sheet.usePreset')}</button>
        </div>
        <textarea className="min-h-16 w-full rounded-md border bg-transparent p-2 font-mono text-xs outline-none" value={mapping} onChange={(e) => setMapping(e.target.value)} />
      </div>
      <div className="flex justify-end">
        <Button onClick={() => void submit()} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sheet className="h-4 w-4" />} {t('addKnowledge.sheet.import')}
        </Button>
      </div>
    </div>
  )
}

// ── Chọn sản phẩm cho AI (bật / tắt) ────────────────────────────────────────────

function ProductPickerInline() {
  const { t } = useTranslation('aiKnow')
  const [search, setSearch] = useState('')
  const { data, isLoading } = useProducts({ search: search || undefined, pageSize: 50 })
  const update = useUpdateProduct()

  async function toggleAi(id: string, on: boolean) {
    try {
      await update.mutateAsync({ id, data: { status: on ? 'active' : 'draft' } })
      toast.success(on ? t('addKnowledge.product.enabled') : t('addKnowledge.product.disabled'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="space-y-2 pt-1">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Database className="h-3.5 w-3.5" />
        {t('addKnowledge.product.hint')}
      </p>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" placeholder={t('addKnowledge.product.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border p-2">
        {isLoading ? (
          <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {t('addKnowledge.product.loading')}</div>
        ) : (data?.items ?? []).length === 0 ? (
          <p className="p-2 text-xs text-muted-foreground">{t('addKnowledge.product.none')}</p>
        ) : (data?.items ?? []).map((p) => (
          <div key={p.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{p.name}</p>
              <p className="text-[11px] text-muted-foreground">{formatProductPrice(p)}</p>
            </div>
            {isSyncedProduct(p) ? (
              // Sản phẩm đồng bộ: chỉ đọc — bật/tắt ở hệ thống gốc (hoặc chọn nguồn ở màn Train).
              <Badge variant="outline" title={t('addKnowledge.product.readOnly')}>
                {t('addKnowledge.product.syncedFrom', { source: PRODUCT_SOURCE_LABELS[p.externalSource!] ?? p.externalSource })}
              </Badge>
            ) : p.status === 'archived' ? (
              <Badge variant="secondary">{t('addKnowledge.product.archived')}</Badge>
            ) : (
              <Switch checked={p.status === 'active'} onCheckedChange={(v) => void toggleAi(p.id, v)} />
            )}
          </div>
        ))}
      </div>
      <Separator />
      <p className="text-[11px] text-muted-foreground">
        {t('addKnowledge.product.total', { n: data?.meta.total ?? 0 })}
      </p>
    </div>
  )
}
